/**
 * Vibration Manager
 * Manages vibration patterns for mobile devices using the Vibration API
 */

export type VibrationType = 'approved' | 'rejected' | 'assigned' | 'general';

export interface VibrationSettings {
  enabled: boolean;
}

// Vibration patterns (in milliseconds)
// Pattern format: [vibrate, pause, vibrate, pause, ...]
const VIBRATION_PATTERNS: Record<VibrationType, number | number[]> = {
  approved: [100, 50, 100], // Double short vibration
  rejected: [200, 100, 200, 100, 200], // Triple medium vibration
  assigned: [150], // Single medium vibration
  general: [50], // Single short vibration
};

const STORAGE_KEY = 'notification_vibration_settings';

class VibrationManager {
  private settings: VibrationSettings;
  private isSupported: boolean;

  constructor() {
    // Check if Vibration API is supported
    this.isSupported = 'vibrate' in navigator;
    
    // Load settings from localStorage
    this.settings = this.loadSettings();
  }

  /**
   * Check if Vibration API is supported
   */
  isVibrationSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Load vibration settings from localStorage
   */
  private loadSettings(): VibrationSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load vibration settings:', error);
    }
    
    // Default settings
    return {
      enabled: true,
    };
  }

  /**
   * Save vibration settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save vibration settings:', error);
    }
  }

  /**
   * Trigger vibration
   */
  vibrate(type: VibrationType): void {
    if (!this.isSupported) {
      console.warn('Vibration API is not supported on this device');
      return;
    }

    if (!this.settings.enabled) {
      return;
    }

    const pattern = VIBRATION_PATTERNS[type];
    if (!pattern) {
      console.warn(`Vibration pattern not found for type: ${type}`);
      return;
    }

    try {
      navigator.vibrate(pattern);
    } catch (error) {
      console.error('Failed to trigger vibration:', error);
    }
  }

  /**
   * Stop any ongoing vibration
   */
  stop(): void {
    if (!this.isSupported) {
      return;
    }

    try {
      navigator.vibrate(0);
    } catch (error) {
      console.error('Failed to stop vibration:', error);
    }
  }

  /**
   * Get current vibration settings
   */
  getSettings(): VibrationSettings {
    return { ...this.settings };
  }

  /**
   * Update vibration settings
   */
  updateSettings(settings: Partial<VibrationSettings>): void {
    this.settings = {
      ...this.settings,
      ...settings,
    };
    this.saveSettings();
  }

  /**
   * Enable/disable vibration
   */
  setEnabled(enabled: boolean): void {
    this.updateSettings({ enabled });
  }

  /**
   * Test vibration (for settings preview)
   */
  testVibrate(type: VibrationType): void {
    // Temporarily enable vibration for testing
    const wasEnabled = this.settings.enabled;
    this.settings.enabled = true;
    this.vibrate(type);
    this.settings.enabled = wasEnabled;
  }
}

// Export singleton instance
export const vibrationManager = new VibrationManager();
